import { ApplicationSubStatus } from '../../../utils/applicationSubStatus'
import { LeasePet } from '../../../models/Lease/types'

export type PipelineTabId = string

export type PipelineStage =
    | 'leads'
    | 'tours'
    | 'app_sent'
    | 'screening'
    | 'leases'
    | 'lease_sent'
    | 'lease_signed'
    | 'guarantor'
    | 'future_resident'

export const PIPELINE_STAGES: { id: PipelineStage; label: string }[] = [
    { id: 'leads', label: 'Leads' },
    { id: 'tours', label: 'Tours' },
    { id: 'app_sent', label: 'Applications' },
    { id: 'screening', label: 'Screening' },
    { id: 'leases', label: 'Leases' },
    { id: 'lease_sent', label: 'Lease Sent' },
    { id: 'lease_signed', label: 'Lease Signed' },
    { id: 'guarantor', label: 'Guarantor' },
    { id: 'future_resident', label: 'Future Resident' },
]

export const PIPELINE_STAGE_LABEL: Record<
    PipelineStage,
    string
> = PIPELINE_STAGES.reduce((acc, s) => {
    acc[s.id] = s.label
    return acc
}, {} as Record<PipelineStage, string>)

export type PipelineOwner = 'ai' | 'human'
export type AutomationState = 'active' | 'paused' | 'resumed'
export type PipelineSource =
    | 'website'
    | 'form'
    | 'call'
    | 'referral'
    | 'walk_in'
    | 'campaign'

export type TourPipelineStatus =
    | 'scheduled'
    | 'completed'
    | 'no_show'
    | 'skipped'

export interface PipelineRecord {
    id: string
    leadId?: number
    name: string
    email: string
    phone: string
    stage: PipelineStage
    tabId?: string
    tabLabel?: string
    owner: PipelineOwner
    automation: AutomationState
    source: PipelineSource
    unit_interest?: string
    beds?: number
    budget?: number
    intent_score?: number
    tour_status?: TourPipelineStatus
    tour_date?: string
    tour_staff?: string
    tour_id?: number
    app_sent_date?: string
    app_progress?: number
    reminders_sent?: number
    pending_application_review?: boolean
    screening_result?:
        | 'pending'
        | 'approved'
        | 'approved_prior_resident'
        | 'conditional'
        | 'declined'
    credit_score?: number
    background_check?: 'pending' | 'clear' | 'consider'
    screening_status?:
        | 'PENDING'
        | 'INVITED'
        | 'PROCESSING'
        | 'COMPLETE'
        | 'FAILED'
        | 'EXPIRED'
    screening_invitation_url?: string
    screening_report_url?: string
    screening_candidate_id?: string
    can_start_screening?: boolean
    can_resend_screening?: boolean
    can_send_screening_link?: boolean
    can_send_lease_package?: boolean
    preferred_unit_config_id?: number
    preferred_premium_ids?: number[]
    lease_packet_preview?: Array<{
        name: string | null
        included: boolean
    }>
    income_ratio?: number
    risk?: 'low' | 'medium' | 'high'
    ai_recommendation?: string
    guarantor_status?: 'needed' | 'invited' | 'submitted' | 'approved'
    guarantor_progress?: number
    guarantor_requested?: string
    lease_sent_date?: string
    lease_progress?: number
    missing_signatures?: string[]
    lease_signed_date?: string
    lease_package_status?: 'sent' | 'awaiting_property' | 'signed'
    lease_awaiting_property?: boolean
    outstanding_items?: string[]
    handoff_status?: 'pending' | 'complete'
    move_in_date?: string
    checklist_done?: number
    checklist_total?: number
    isRealLead?: boolean
    application_status?: ApplicationSubStatus | null
    application_fee_cents?: number
    application_fee_paid_at?: string
    application_fee_status?: string | null
    qualifying_method?: string | null
    workbook_sections_complete?: number | null
    workbook_sections_total?: number | null
    workbook_last_activity_at?: string | null
    has_submission_pdf?: boolean
    account_status?: 'none' | 'invited' | 'active'
    invitation_sent_at?: string | null
    account_activated_at?: string | null
    user_id?: number | null
    /** Most recent known touchpoint for Last activity column. */
    last_activity_at?: string | null
    next_action?: {
        waiting_on: 'them' | 'us' | 'you' | null
        label: string | null
        waiting_since?: string | null
    } | null
    pets?: LeasePet[]
}

export const PIPELINE_SOURCE_LABEL: Record<PipelineSource, string> = {
    website: 'Website',
    form: 'In-Person Form',
    call: 'Phone Call',
    referral: 'Referral',
    walk_in: 'Walk-in',
    campaign: 'Campaign',
}

export const QUALIFYING_METHOD_LABEL: Record<string, string> = {
    self_qualified: 'Self',
    personal_guarantor: 'Guarantor',
    leap_gwp: 'Leap / GWP',
    prepayment: 'Prepayment',
}
